/**
 * Sentry.io Helper Functions for GermanPath
 * 
 * This file contains helper functions for error tracking and performance monitoring.
 * Sentry is only active in production mode.
 */

import * as Sentry from '@sentry/react';

// Types for vocabulary context
interface VocabularyContext {
  wordId?: string;
  arabic?: string;
  listId?: string;
  audioVoice?: string;
}

// Types for exercise context
interface ExerciseContext {
  exerciseType: 'flashcard' | 'multiple-choice' | 'typing' | 'word-pairs' | 'listening' | 'dialogue';
  listId?: string;
  sessionSize?: number;
}

// Known iOS issue types
type KnownIssueType = 
  | 'ios-audio-autoplay'
  | 'safari-orientation'
  | 'pwa-cache'
  | 'browser-tts-fallback';

/**
 * Check if Sentry is enabled (production only)
 */
export function isSentryEnabled(): boolean {
  return import.meta.env.PROD;
}

/**
 * Set vocabulary context for better error tracking
 * Call before audio operations or vocabulary-related actions
 */
export function setVocabularyContext(context: VocabularyContext): void {
  if (!isSentryEnabled()) return;
  
  Sentry.setContext('vocabulary', {
    wordId: context.wordId,
    arabic: context.arabic?.substring(0, 50), // Limit length
    listId: context.listId,
    audioVoice: context.audioVoice,
  });
  
  // Also set as tags for filtering
  if (context.listId) {
    Sentry.setTag('vocabulary.listId', context.listId);
  }
  if (context.audioVoice) {
    Sentry.setTag('vocabulary.voice', context.audioVoice);
  }
}

/**
 * Set exercise context for session tracking
 * Call when starting an exercise session
 */
export function setExerciseContext(context: ExerciseContext): void {
  if (!isSentryEnabled()) return;
  
  Sentry.setContext('exercise', {
    type: context.exerciseType,
    listId: context.listId,
    sessionSize: context.sessionSize,
  });
  
  Sentry.setTag('exercise.type', context.exerciseType);
  if (context.listId) {
    Sentry.setTag('exercise.listId', context.listId);
  }
}

/**
 * Clear exercise context when session ends
 */
export function clearExerciseContext(): void {
  if (!isSentryEnabled()) return;
  
  Sentry.setContext('exercise', null);
}

/**
 * Capture known iOS issues with reduced severity
 * These are expected issues that we can't fully fix
 */
export function captureKnownIssue(
  error: Error,
  issueType: KnownIssueType,
  extra?: Record<string, unknown>
): void {
  if (!isSentryEnabled()) return;
  
  Sentry.withScope((scope) => {
    scope.setTag('known.issue', issueType);
    scope.setLevel('warning');
    scope.setFingerprint(['known-issue', issueType]);
    
    if (extra) {
      scope.setExtras(extra);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Add custom breadcrumb for user actions
 */
export function addBreadcrumb(
  category: 'audio' | 'exercise' | 'navigation' | 'payment' | 'auth' | 'vocabulary',
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void {
  if (!isSentryEnabled()) return;
  
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  });
}

/**
 * Capture API/TTS errors with additional context
 */
export function captureApiError(
  error: Error,
  operation: 'tts' | 'supabase' | 'stripe' | 'fetch',
  context?: {
    endpoint?: string;
    statusCode?: number;
    table?: string;
    action?: string;
  }
): void {
  if (!isSentryEnabled()) return;
  
  Sentry.withScope((scope) => {
    scope.setTag('api.operation', operation);
    
    if (context?.endpoint) {
      scope.setTag('api.endpoint', context.endpoint);
    }
    if (context?.statusCode) {
      scope.setTag('api.statusCode', String(context.statusCode));
    }
    if (context?.table) {
      scope.setTag('supabase.table', context.table);
    }
    if (context?.action) {
      scope.setTag('api.action', context.action);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Capture payment-related errors with special handling
 */
export function capturePaymentError(
  error: Error,
  action: 'checkout' | 'subscription' | 'portal' | 'webhook',
  extra?: Record<string, unknown>
): void {
  if (!isSentryEnabled()) return;
  
  Sentry.withScope((scope) => {
    scope.setTag('payment.action', action);
    scope.setLevel('error');
    
    if (extra) {
      // Filter out sensitive payment data
      const safeExtra = { ...extra };
      delete safeExtra.cardNumber;
      delete safeExtra.cvv;
      delete safeExtra.customerId;
      scope.setExtras(safeExtra);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Track performance of async operations
 */
export function startSpan<T>(
  name: string,
  operation: string,
  callback: () => Promise<T>
): Promise<T> {
  if (!isSentryEnabled()) {
    return callback();
  }
  
  return Sentry.startSpan(
    { name, op: operation },
    callback
  );
}

/**
 * beforeSend filter to sanitize data before sending to Sentry
 */
export function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  const error = hint.originalException;
  
  // Ignore offline network errors (don't spam Sentry when user is offline)
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('load failed') ||
      message.includes('internet disconnected')
    ) {
      // Check if actually offline
      if (!navigator.onLine) {
        return null;
      }
    }
    
    // Ignore ResizeObserver loop errors (common, harmless)
    if (message.includes('resizeobserver loop')) {
      return null;
    }
  }
  
  // Sanitize sensitive data from headers
  if (event.request?.headers) {
    const headers = { ...event.request.headers };
    delete headers['Authorization'];
    delete headers['authorization'];
    delete headers['Cookie'];
    delete headers['cookie'];
    event.request.headers = headers;
  }
  
  // Remove email addresses from user data (we only want user id)
  if (event.user?.email) {
    delete event.user.email;
  }
  
  // Add deployment metadata
  event.tags = {
    ...event.tags,
    'app.version': import.meta.env.VITE_APP_VERSION || 'unknown',
  };
  
  return event;
}

/**
 * Set user context after authentication
 * Only sends non-sensitive information
 */
export function setUser(userId: string | null, username?: string | null): void {
  if (!isSentryEnabled()) return;
  
  if (userId) {
    Sentry.setUser({
      id: userId,
      username: username || undefined,
      // Never send email for privacy
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Show user feedback dialog after an error
 * Uses Sentry v9 Feedback API
 */
export function showFeedbackDialog(): void {
  if (!isSentryEnabled()) return;
  
  // In Sentry v9, feedback is triggered via the captureFeedback API
  // or by embedding the widget. For now, we'll use captureMessage with feedback scope.
  // The actual feedback widget is auto-injected via feedbackIntegration.
  const client = Sentry.getClient();
  if (client) {
    // Trigger the feedback form if available
    const feedback = Sentry.getFeedback();
    if (feedback && typeof feedback.createWidget === 'function') {
      const widget = feedback.createWidget();
      // Widget will auto-open on creation
    }
  }
}
